/**
 * Routing Engine
 * Движок автоматической маршрутизации обращений
 */

import type {
  RoutingRule,
  RoutingRequest,
  RoutingResult,
  RoutingCondition,
  RoutingAction,
  ProcessingQueue,
  AgentSkill,
  AssignmentStrategy
} from './types'
import {
  RoutingConditionType,
  RoutingOperator,
  RoutingActionType
} from './types'

/**
 * Evaluate routing rules and return best match
 */
export function evaluateRoutingRules(
  request: RoutingRequest,
  rules: RoutingRule[]
): RoutingResult | null {
  // Filter active rules
  const activeRules = rules.filter((rule) => rule.isActive)

  // Sort by priority (lower number = higher priority)
  const sortedRules = activeRules.sort((a, b) => a.priority - b.priority)

  // Find first matching rule
  for (const rule of sortedRules) {
    if (evaluateConditions(request, rule.conditions)) {
      return executeActions(rule, request)
    }
  }

  return null
}

/**
 * Evaluate all conditions for a rule
 */
function evaluateConditions(
  request: RoutingRequest,
  conditions: RoutingCondition[]
): boolean {
  // All conditions must be true (AND logic)
  return conditions.every((condition) => evaluateCondition(request, condition))
}

/**
 * Evaluate a single condition
 */
function evaluateCondition(
  request: RoutingRequest,
  condition: RoutingCondition
): boolean {
  const { type, operator, value } = condition

  let actualValue: any

  // Get actual value from request
  switch (type) {
    case RoutingConditionType.CATEGORY:
      actualValue = request.category
      break
    case RoutingConditionType.SUBCATEGORY:
      actualValue = request.subcategory
      break
    case RoutingConditionType.PRIORITY:
      actualValue = request.priority
      break
    case RoutingConditionType.CHANNEL:
      actualValue = request.channel
      break
    case RoutingConditionType.CUSTOMER_SEGMENT:
      actualValue = request.customerSegment
      break
    case RoutingConditionType.ACCOUNT_STATUS:
      actualValue = request.accountStatus
      break
    case RoutingConditionType.TIME_OF_DAY:
      actualValue = request.timestamp
        ? new Date(request.timestamp).getHours()
        : new Date().getHours()
      break
    case RoutingConditionType.DAY_OF_WEEK:
      actualValue = request.timestamp
        ? new Date(request.timestamp).getDay()
        : new Date().getDay()
      break
    case RoutingConditionType.KEYWORDS:
      actualValue = request.description?.toLowerCase() || ''
      break
    default:
      return false
  }

  // Apply operator
  switch (operator) {
    case RoutingOperator.EQUALS:
      return actualValue === value

    case RoutingOperator.NOT_EQUALS:
      return actualValue !== value

    case RoutingOperator.IN:
      return Array.isArray(value) && value.includes(actualValue)

    case RoutingOperator.NOT_IN:
      return Array.isArray(value) && !value.includes(actualValue)

    case RoutingOperator.CONTAINS:
      return (
        typeof actualValue === 'string' &&
        typeof value === 'string' &&
        actualValue.includes(value.toLowerCase())
      )

    case RoutingOperator.GREATER_THAN:
      return (
        typeof actualValue === 'number' &&
        typeof value === 'number' &&
        actualValue > value
      )

    case RoutingOperator.LESS_THAN:
      return (
        typeof actualValue === 'number' &&
        typeof value === 'number' &&
        actualValue < value
      )

    default:
      return false
  }
}

/**
 * Execute actions from a matched rule
 */
function executeActions(
  rule: RoutingRule,
  request: RoutingRequest
): RoutingResult {
  const result: RoutingResult = {
    ruleId: rule.id,
    ruleName: rule.name
  }

  for (const action of rule.actions) {
    switch (action.type) {
      case RoutingActionType.ASSIGN_TO_QUEUE:
        result.queueId = action.value as string
        break

      case RoutingActionType.ASSIGN_TO_AGENT:
        result.agentId = action.value as string
        break

      case RoutingActionType.SET_PRIORITY:
        result.priority = action.value as 'high' | 'mid' | 'low'
        break

      case RoutingActionType.ADD_TAG:
        result.tags = Array.isArray(action.value)
          ? action.value
          : [action.value as string]
        break

      case RoutingActionType.SEND_NOTIFICATION:
        result.notifications = Array.isArray(action.value)
          ? action.value
          : [action.value as string]
        break
    }
  }

  return result
}

/**
 * Auto-assign ticket to agent in queue
 */
export function autoAssignToAgent(
  queue: ProcessingQueue,
  agentSkills: AgentSkill[],
  requiredSkills?: string[]
): string | null {
  if (!queue.autoAssign || queue.agentIds.length === 0) {
    return null
  }

  switch (queue.assignmentStrategy) {
    case 'round_robin':
      return assignRoundRobin(queue.agentIds)

    case 'least_loaded':
      return assignLeastLoaded(queue.agentIds)

    case 'skill_based':
      return assignSkillBased(queue.agentIds, agentSkills, requiredSkills)

    case 'random':
      return assignRandom(queue.agentIds)

    default:
      return queue.agentIds[0] || null
  }
}

/**
 * Round-robin assignment
 */
let roundRobinIndex = 0

function assignRoundRobin(agentIds: string[]): string | null {
  if (agentIds.length === 0) return null
  const agentId = agentIds[roundRobinIndex % agentIds.length] || null
  roundRobinIndex++
  return agentId
}

/**
 * Assign to least loaded agent
 * TODO: Integrate with actual agent workload tracking
 */
function assignLeastLoaded(agentIds: string[]): string | null {
  // For now, just return first agent
  // In production, this should query agent workload
  if (agentIds.length === 0) return null
  return agentIds[0] || null
}

/**
 * Assign based on skills
 */
function assignSkillBased(
  agentIds: string[],
  agentSkills: AgentSkill[],
  requiredSkills?: string[]
): string | null {
  if (agentIds.length === 0) return null

  if (!requiredSkills || requiredSkills.length === 0) {
    return agentIds[0] || null
  }

  // Find agents with required skills
  const qualifiedAgents = agentIds.filter((agentId) => {
    const skills = agentSkills.find((s) => s.agentId === agentId)
    if (!skills) return false

    return requiredSkills.every((skill) => skills.skills.includes(skill))
  })

  if (qualifiedAgents.length === 0) {
    return null
  }

  // Return agent with highest proficiency
  const bestAgent = qualifiedAgents.reduce((best, current) => {
    const currentSkills = agentSkills.find((s) => s.agentId === current)
    const bestSkills = agentSkills.find((s) => s.agentId === best)

    if (!currentSkills || !bestSkills) return best

    const currentTotal = requiredSkills.reduce(
      (sum, skill) => sum + (currentSkills.proficiencyLevel[skill] || 0),
      0
    )

    const bestTotal = requiredSkills.reduce(
      (sum, skill) => sum + (bestSkills.proficiencyLevel[skill] || 0),
      0
    )

    return currentTotal > bestTotal ? current : best
  })

  return bestAgent
}

/**
 * Random assignment
 */
function assignRandom(agentIds: string[]): string | null {
  if (agentIds.length === 0) return null
  const randomIndex = Math.floor(Math.random() * agentIds.length)
  return agentIds[randomIndex] || null
}

/**
 * Check if queue has capacity
 */
export function hasQueueCapacity(queue: ProcessingQueue): boolean {
  if (!queue.maxCapacity) return true
  return queue.currentLoad < queue.maxCapacity
}

/**
 * Calculate queue load percentage
 */
export function calculateQueueLoad(queue: ProcessingQueue): number {
  if (!queue.maxCapacity) return 0
  return (queue.currentLoad / queue.maxCapacity) * 100
}
